/**
 * Engineering Library — single entry detail page.
 *
 * Public read surface for ANY staff member (rep, technician, owner).
 * Renders the resolved report, NEVER the underlying chat (the chat
 * stays private to the session owner).
 *
 * Two power actions:
 *   • Generate Another — re-runs the spec engine on the original
 *     transcript with a chosen audience+length.  Polls until ready,
 *     then swaps the visible report in-place and saves the new export
 *     to the library (owned by the requesting user).
 *   • Discuss This — spawns a fresh engineering session owned by the
 *     requesting user, seeded with this report as Hidro's opening
 *     message.  Navigates to the new session.
 *
 * Viewing this page marks the entry as read for the current user
 * (engineering_library_reads collection) — feeds the upcoming
 * reading-leaderboard.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  BookOpen, Sparkles, MessageSquare, Loader2,
  User as UserIcon, Calendar, Tag,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import HidroMarkdown from "../components/HidroMarkdown";
import { EntityShell } from "../components/shell";

const AUDIENCES = [
  { k: "technician", label: "Technician", hint: "Action-oriented, jargon OK" },
  { k: "engineer", label: "Engineer", hint: "Math, standards, tolerances" },
  { k: "customer", label: "Customer", hint: "Plain language, no jargon" },
  { k: "blog", label: "Blog post", hint: "Public, fully anonymised" },
];
const LENGTHS = [
  { k: "short", label: "Short", hint: "< 200 words" },
  { k: "medium", label: "Medium", hint: "400–600 words" },
  { k: "long", label: "Long", hint: "1000–1500 words" },
  { k: "exhaustive", label: "Exhaustive", hint: "2500+ words, treatise" },
];

export default function EngineeringLibraryDetail() {
  const { kind, id } = useParams();
  const nav = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGenerator, setShowGenerator] = useState(false);
  const [audience, setAudience] = useState("engineer");
  const [length, setLength] = useState("medium");
  const [generating, setGenerating] = useState(false);
  const [genId, setGenId] = useState(null);
  const [discussing, setDiscussing] = useState(false);

  // Fetch + mark-as-read on every mount.  The BE upserts the
  // read record so we don't need to dedupe FE-side.
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/engineering/library/${kind}/${id}`);
      setEntry(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load library entry.");
      setEntry(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [kind, id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Poll the generator background task until status flips.
  useEffect(() => {
    if (!genId) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/engineering/library-generations/${genId}`);
        if (cancelled) return;
        if (data.status === "ready") {
          toast.success("New report ready.");
          setGenerating(false);
          setGenId(null);
          // Swap the visible entry to the new export — same page
          // pattern, owned by the requesting user.  We use replace
          // (not push) so the back button still goes to the library list.
          nav(`/tools/engineering-desk/library/export/${data.id}`, { replace: true });
        } else if (data.status === "error") {
          toast.error(data.body || "Generation failed.");
          setGenerating(false);
          setGenId(null);
        }
      } catch (_e) { /* keep polling */ }
    };
    const handle = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [genId, nav]);

  const generateAnother = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/engineering/library/${kind}/${id}/generate`,
        { audience, length, format: "markdown" },
      );
      toast.message(`Hidro is composing a ${length} ${audience} report — appears in ~30-90s.`);
      setGenId(data.id);
      setShowGenerator(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start generation.");
      setGenerating(false);
    }
  };

  const discussThis = async () => {
    setDiscussing(true);
    try {
      const { data } = await api.post(`/engineering/library/${kind}/${id}/discuss`);
      toast.success("New discussion started.");
      nav(`/tools/engineering-desk/${data.session_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start discussion.");
    } finally {
      setDiscussing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[#647087]" data-testid="library-detail-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (!entry) {
    return (
      <div className="hv-card p-6 text-center text-sm text-[#a0aabf]" data-testid="library-detail-missing">
        This library entry could not be loaded.
        <div className="mt-3">
          <Link to="/tools/engineering-desk" className="text-[#1FB6C1] hover:underline">
            ← Back to Engineering Desk
          </Link>
        </div>
      </div>
    );
  }

  const cat = (entry.category_key || "general").replaceAll("_", " ");
  const date = entry.resolved_at ? new Date(entry.resolved_at).toLocaleDateString("en-GB") : "";

  const headerActions = (
    <>
      <button
        onClick={() => setShowGenerator((v) => !v)}
        className="hv-btn hv-btn-primary text-xs"
        disabled={generating}
        data-testid="library-detail-generate-toggle"
      >
        <Sparkles className="w-3.5 h-3.5" /> Generate another
      </button>
      <button
        onClick={discussThis}
        className="hv-btn hv-btn-ghost text-xs"
        disabled={discussing}
        data-testid="library-detail-discuss"
      >
        {discussing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
        Discuss
      </button>
    </>
  );

  return (
    <EntityShell
      kindLabel={`Library · ${cat}`}
      title={entry.headline || entry.title}
      status={entry.kind === "export" && entry.audience ? {
        label: `${entry.audience} · ${entry.length}`,
        tone: "green",
      } : undefined}
      backTo="/tools/engineering-desk"
      actions={headerActions}
    >
    <div className="space-y-5 max-w-3xl" data-testid="library-detail">

      <div className="hv-card p-5">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span
            className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded bg-[#1FB6C1]/15 text-[#1FB6C1] border border-[#1FB6C1]/30"
            data-testid="library-detail-category"
          >
            <Tag className="w-3 h-3 inline mr-1" /> {cat}
          </span>
          {entry.is_author && (
            <span
              className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded text-purple-300 border border-purple-500/40 bg-purple-500/10"
              data-testid="library-detail-author-chip"
            >
              You wrote this
            </span>
          )}
        </div>

        {/* Byline — prominent so Dirk can see at a glance who wrote it */}
        <div className="flex items-center gap-3 text-xs text-[#a0aabf]">
          <div className="flex items-center gap-1.5">
            <span
              className="w-7 h-7 rounded-full bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 flex items-center justify-center text-[11px] font-bold text-[#1FB6C1]"
              data-testid="library-detail-author-avatar"
            >
              {(entry.owner_name || "?").slice(0, 1).toUpperCase()}
            </span>
            <span className="font-medium text-[#cbd2df]" data-testid="library-detail-author-name">
              {entry.owner_name || "Unknown"}
            </span>
          </div>
          {date && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {date}
            </span>
          )}
        </div>

        {/* Inline generator — no modals (No-Modal contract) */}
        {showGenerator && (
          <div className="mt-4 pt-4 border-t border-white/10 space-y-3" data-testid="library-detail-generator">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1.5">Audience</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 hv-keep-cols">
                {AUDIENCES.map((a) => (
                  <button
                    key={a.k}
                    type="button"
                    onClick={() => setAudience(a.k)}
                    className={`text-left p-2 rounded border text-xs transition ${
                      audience === a.k
                        ? "border-[#1FB6C1] bg-[#1FB6C1]/10"
                        : "border-white/10 bg-white/[0.02] hover:border-white/25"
                    }`}
                    data-testid={`library-audience-${a.k}`}
                  >
                    <div className="font-medium text-white">{a.label}</div>
                    <div className="text-[10px] text-[#647087] mt-0.5">{a.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1.5">Length</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 hv-keep-cols">
                {LENGTHS.map((l) => (
                  <button
                    key={l.k}
                    type="button"
                    onClick={() => setLength(l.k)}
                    className={`text-left p-2 rounded border text-xs transition ${
                      length === l.k
                        ? "border-[#1FB6C1] bg-[#1FB6C1]/10"
                        : "border-white/10 bg-white/[0.02] hover:border-white/25"
                    }`}
                    data-testid={`library-length-${l.k}`}
                  >
                    <div className="font-medium text-white">{l.label}</div>
                    <div className="text-[10px] text-[#647087] mt-0.5">{l.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowGenerator(false)}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="library-generator-cancel"
              >
                Cancel
              </button>
              <button
                onClick={generateAnother}
                disabled={generating}
                className="hv-btn hv-btn-primary text-xs"
                data-testid="library-generator-submit"
              >
                {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Generate {length} {audience} report
              </button>
            </div>
          </div>
        )}

        {/* Running indicator while bg worker is busy */}
        {generating && genId && (
          <div className="mt-3 bg-[#1FB6C1]/8 border border-[#1FB6C1]/30 rounded p-3 text-xs text-[#a0aabf] flex items-center gap-2" data-testid="library-generating">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1FB6C1]" />
            Hidro is composing your {length} {audience} report. This usually takes 30-90s for short/medium, up to 3 minutes for exhaustive. Feel free to keep browsing.
          </div>
        )}
      </div>

      {/* Body */}
      <div className="hv-card p-5">
        {entry.tldr && (
          <div className="mb-4 pb-4 border-b border-white/10">
            <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">TL;DR</div>
            <div className="text-sm text-[#cbd2df]" data-testid="library-detail-tldr">{entry.tldr}</div>
          </div>
        )}
        <HidroMarkdown text={entry.body || ""} className="text-sm" testId="library-detail-body" />
        {(entry.tags || []).length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-1.5">
            {(entry.tags || []).filter(Boolean).map((t, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.05] text-[#a0aabf] border border-white/10">
                #{t}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
    </EntityShell>
  );
}
